import React, { useContext, useEffect, useMemo, useState } from "react";
import { useStaticQuery } from "gatsby";
import { useTranslation } from "react-i18next";

import buttonStyle from "../../styles/addToCartButton.module.scss";
import { ProductBadge } from "./ProductBadge";
import { ProductPrice } from "./ProductPrice";
import { ProductTitle } from "./ProductTitle";
import { addToCompare } from "./compareHelpers";
import { graphql } from "gatsby";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { productLiveAvailabilityLabel } from "../../utils/productAvailabilityInfo";
import { IProduct } from "@sbi-international/boutik.gatsby.plugin.services";
import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services/dist/src/contexts/servicesContext";
import { Button, LocalizedLink } from "../primitives";
import GatsbyImageWrapper from "../GatsbyImageWrapper";
import { ProductReviewsResume } from "../../models/productReviewsResume";
import { ProductStock } from "@sbi-international/boutik.gatsby.plugin.components";

type CategoryProductListingSingleProps = {
    product: any;
    review?: ProductReviewsResume;
    isDiscontinued: boolean;
    isComparable?: boolean;
    compareArray?: any[];
    setCompareArray?: (values: any[]) => void;
};

export default function CategoryProductListingSingle(props: CategoryProductListingSingleProps) {
    const { product, isDiscontinued, isComparable, compareArray, setCompareArray, review } = props;

    const [productData, setProductData] = useState<IProduct>();
    const [availabilityLabel, setAvailabilityLabel] = useState<string>("");

    const [reviewToShow, setReviewToShow] = useState<ProductReviewsResume>();

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    const allProducts = useStaticQuery(
        graphql`
            query {
                allProductsJson {
                    nodes {
                        code
                        images
                        fields {
                            ...productsFieldsImagesFragment
                        }
                    }
                }
                allProductReviewsJson {
                    nodes {
                        sku
                        average
                        count
                    }
                }
            }
        `
    );

    const allReviews = allProducts.allProductReviewsJson.nodes;

    const products = allProducts.allProductsJson.nodes;
    const product2 = useMemo(
        () => products.find((product2: any) => product2.code === product.code),
        [products, product]
    );

    const { currentLocale, navigateTranslated } = usePageLocalization();
    const { t } = useTranslation("templatePages");

    const removeFirstPathname = (slug: any) => {
        let newSlug: any = {};
        if (slug) {
            for (const [key, value] of Object.entries(slug)) {
                const slugProps = value as string;
                newSlug[key] = slugProps.split("/").slice(2).join("/") + "/";
            }
            return newSlug;
        } else {
            return slug;
        }
    };

    const surfiCurrentLocale = product.attributes?.Surfi?.values[0][currentLocale];
    const pwMaSoiCurrentLocale = product.attributes?.PWMaSoi?.values[0][currentLocale];

    const linkToProductPage = isDiscontinued
        ? `/archives/${product?.code?.toLowerCase()}/parts/`
        : removeFirstPathname(product.slug);

    useEffect(() => {
        productService.operations.getProduct(product.code).then((productLive) => {
            if (productLive) {
                setProductData(productLive);
                const availabilityInfo = productLiveAvailabilityLabel(
                    productLive.currentStock,
                    t,
                    product.attributes?.custom_made?.values?.[0]?.[currentLocale] ?? false
                );
                setAvailabilityLabel(availabilityInfo);
            }
        });
    }, [product]);

    useEffect(() => {
        if (review) {
            setReviewToShow(review);
        } else {
            const newReview = allReviews.find((r: any) => r.sku === product.code);
            setReviewToShow(newReview);
        }
    }, [review]);

    return (
        <div className="categoryProductListingSingle">
            <LocalizedLink className="productListingWrap" to={linkToProductPage}>
                <>
                    <div className="product-img-wrap">
                        <ProductBadge product={product} />
                        <GatsbyImageWrapper className="product-image" fields={product2.fields} />
                        {isComparable && compareArray && setCompareArray && (
                            <Button
                                className="compareBtn"
                                label={t("compare") as string}
                                onClick={(event) =>
                                    addToCompare(event, product, setCompareArray, compareArray)
                                }
                            />
                        )}
                    </div>

                    <div className="product-info">
                        <div className="minHeight">
                            <div className="title-div">
                                {" "}
                                {product.title && (
                                    <ProductTitle product={product} reviews={reviewToShow} />
                                )}
                            </div>
                            <div className="sku">{product.code}</div>
                            <ul>
                                {surfiCurrentLocale && <li>{surfiCurrentLocale}</li>}
                                {pwMaSoiCurrentLocale && <li>{pwMaSoiCurrentLocale}</li>}
                            </ul>
                            <div className="line"></div>

                            {productData?.currentPrice && (
                                <ProductPrice price={productData?.currentPrice} />
                            )}
                            {productData?.currentStock && (
                                <ProductStock
                                    type="medium"
                                    currentStock={productData?.currentStock}
                                    isCustomMade={false}
                                />
                            )}
                        </div>

                        <Button
                            className={`${buttonStyle.AddToCart}`}
                            label={t("pages:view-product") as string}
                            onClick={() => navigateTranslated(linkToProductPage)}
                        />
                    </div>
                </>
            </LocalizedLink>
        </div>
    );
}
